import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('Invalid authentication response');
          navigate('/login');
          return;
        }

        // Exchange session_id for user data AND access token
        const { data } = await api.post('/auth/google/session', {
          session_id: sessionId
        });

        if (data.success && data.user) {
          // Store BOTH access_token and user data for consistent auth
          if (data.access_token) {
            localStorage.setItem('access_token', data.access_token);
          }
          localStorage.setItem('user', JSON.stringify(data.user));
          
          toast.success(`Welcome back, ${data.user.first_name || data.user.email}!`);
          
          // Force page reload to ensure AuthContext picks up new credentials
          const redirectPath = data.user.role === 'tenant' 
            ? '/tenant/dashboard'
            : data.user.role === 'landlord' || data.user.role === 'landlord_admin'
              ? '/landlord/dashboard'
              : data.user.role === 'super_admin' || data.user.role === 'support_agent'
                ? '/admin/console'
                : '/tenant/dashboard';
          
          window.location.href = redirectPath;
        } else {
          throw new Error('Authentication failed');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processSession();
  }, [navigate, location]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-lg text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
